import React, { useState, useEffect, useRef, useMemo } from 'react';
import { motion } from 'framer-motion';

const cardsData = [
  { id: 'restaurant', name: 'Restaurant & Cafe', img: 'https://res.cloudinary.com/dfg8nrgvf/image/upload/v1783858568/Restaurant_utldbc.png' },
  { id: 'hotel', name: 'Hotels & Resorts', img: 'https://res.cloudinary.com/dfg8nrgvf/image/upload/v1783858569/Resorts_n1iqos.png' },
  { id: 'fitness', name: 'Fitness Studios', img: 'https://res.cloudinary.com/dfg8nrgvf/image/upload/v1783858569/Fitness_Studios_gu4tuc.png' },
  { id: 'healthcare', name: 'Healthcare', img: 'https://res.cloudinary.com/dfg8nrgvf/image/upload/v1783858569/Healthcare_dwr8po.png' },
  { id: 'schools', name: 'Schools', img: 'https://res.cloudinary.com/dfg8nrgvf/image/upload/v1783858568/Education_qhovjt.png' },
  { id: 'construction', name: 'Construction', img: 'https://res.cloudinary.com/dfg8nrgvf/image/upload/v1783858568/Construction_zlrfj1.png' },
  { id: 'realestate', name: 'Real Estate', img: 'https://res.cloudinary.com/dfg8nrgvf/image/upload/v1783858568/Real_Estate_vjqa21.png' },
  { id: 'retail', name: 'Retail Businesses', img: 'https://res.cloudinary.com/dfg8nrgvf/image/upload/v1783858569/webgl_uused_i_ux_of_m1ktug.png' },
  { id: 'professional', name: 'Professional Services', img: 'https://res.cloudinary.com/dfg8nrgvf/image/upload/v1783858569/professial_services_vwyp5a.png' },
  { id: 'startups', name: 'Startups', img: 'https://res.cloudinary.com/dfg8nrgvf/image/upload/v1783858568/Start_ups_fzgf3c.png' },
  { id: 'ecommerce', name: 'E-commerce', img: 'https://res.cloudinary.com/dfg8nrgvf/image/upload/v1783858568/E-commerce_u7i5gs.png' },
  { id: 'nonprofit', name: 'Non-Profit Organizations', img: 'https://res.cloudinary.com/dfg8nrgvf/image/upload/v1783858569/Non-Profit_Organizations_sdmuck.png' },
];

export default function IndustrySolutions() {
  const [viewportWidth, setViewportWidth] = useState(1200);
  const [isPaused, setIsPaused] = useState(false);
  const [time, setTime] = useState(0);
  const requestRef = useRef(null);
  const previousTimeRef = useRef(null);

  useEffect(() => {
    const linkId = 'font-mona-sans';
    if (!document.getElementById(linkId)) {
      const preconnect1 = document.createElement('link');
      preconnect1.rel = 'preconnect';
      preconnect1.href = 'https://fonts.googleapis.com';
      const preconnect2 = document.createElement('link');
      preconnect2.rel = 'preconnect';
      preconnect2.href = 'https://fonts.gstatic.com';
      preconnect2.crossOrigin = 'anonymous';
      const link = document.createElement('link');
      link.id = linkId;
      link.rel = 'stylesheet';
      link.href = 'https://fonts.googleapis.com/css2?family=Mona+Sans:wght@400;500;600;700&display=swap';
      document.head.appendChild(preconnect1);
      document.head.appendChild(preconnect2);
      document.head.appendChild(link);
    }
  }, []);

  useEffect(() => {
    const handleResize = () => setViewportWidth(window.innerWidth);
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const isDesktop = viewportWidth >= 1024;
  const isTablet = viewportWidth >= 640 && viewportWidth < 1024;

  const cardWidth = isDesktop ? 165 : isTablet ? 180 : 140;
  const cardHeight = isDesktop ? 178 : isTablet ? 195 : 152;
  const cardGap = isDesktop ? 64 : isTablet ? 32 : 24;
  const stepX = cardWidth + cardGap;
  const yPeak = isDesktop ? 20 : isTablet ? 32 : 30;
  const H = isDesktop ? 220 : isTablet ? 160 : 95;
  const Rmax = isDesktop ? 15 : isTablet ? 12 : 10;

  const extendedCards = useMemo(() => {
    return [...cardsData, ...cardsData, ...cardsData, ...cardsData].map((card, index) => ({
      ...card,
      uniqueId: `${card.id}-${index}`
    }));
  }, []);

  const totalTrackWidth = extendedCards.length * stepX;

  const animate = (currentTime) => {
    if (previousTimeRef.current != null && !isPaused) {
      const deltaTime = currentTime - previousTimeRef.current;
      setTime((prevTime) => (prevTime + deltaTime * 0.045) % totalTrackWidth);
    }
    previousTimeRef.current = currentTime;
    requestRef.current = requestAnimationFrame(animate);
  };

  useEffect(() => {
    requestRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(requestRef.current);
  }, [isPaused, totalTrackWidth]);

  return (
    <section 
      id="industry-solutions" 
      className="relative w-full overflow-hidden flex flex-col justify-between select-none p-0 min-h-0 h-auto lg:h-[1024px] lg:min-h-0 pb-[64px] sm:pb-[80px]"
      style={{ fontFamily: '"Mona Sans", system-ui, sans-serif' }}
    >
      {/* Background Image Container */}
      <div className="absolute inset-x-[8px] md:inset-x-[12px] lg:inset-x-[20px] inset-y-[20px] md:inset-y-[24px] lg:inset-y-[30px] rounded-[24px] sm:rounded-[40px] overflow-hidden bg-[#0d1527] z-0">
        <div className="absolute inset-0 bg-gradient-to-b from-[#0f172a]/15 via-transparent to-[#0f172a]/35 pointer-events-none" />
      </div>

      {/* Floating Cards Carousel */}
      <div className="absolute inset-x-[8px] md:inset-x-[12px] lg:inset-x-[20px] inset-y-[20px] md:inset-y-[24px] lg:inset-y-[30px] rounded-[24px] sm:rounded-[40px] select-none pointer-events-none z-10 overflow-hidden">
        <div 
          className="relative w-full h-[280px] sm:h-[390px] lg:h-[490px]"
          style={{ marginTop: isDesktop ? '224px' : isTablet ? '124px' : '108px' }}
        >
          {extendedCards.map((card, index) => {
            const basePositionArr = index * stepX;
            let x = (basePositionArr - time) % totalTrackWidth;
            if (x < 0) x += totalTrackWidth;

            let finalX = x - (totalTrackWidth / 2) + (viewportWidth / 2);
            
            const centerX = viewportWidth / 2;
            const cardCenterX = finalX + cardWidth / 2;
            const diffFromCenter = cardCenterX - centerX;
            
            const curveSpan = 1000;
            const d = diffFromCenter / (curveSpan / 2);
            
            const yOffset = yPeak + H * d * d;
            const rotation = Math.max(-Rmax, Math.min(Rmax, d * Rmax));
            const scale = 1.05 - 0.12 * d * d;
            const opacity = Math.min(1, Math.max(0, (1.35 - Math.abs(d)) / 0.45));

            return (
              <motion.div
                key={card.uniqueId}
                onMouseEnter={() => setIsPaused(true)}
                onMouseLeave={() => setIsPaused(false)}
                className="absolute shrink-0 pointer-events-auto rounded-[20px] bg-white border border-slate-100/40 overflow-hidden shadow-[0_12px_28px_rgba(0,0,0,0.06)] hover:shadow-[0_20px_40px_rgba(0,0,0,0.12)] cursor-pointer group"
                style={{
                  width: cardWidth,
                  height: cardHeight,
                  left: finalX + "px",
                  top: yOffset + "px",
                  rotate: rotation + "deg",
                  scale,
                  opacity,
                  transition: "box-shadow 0.3s ease, border-color 0.3s ease",
                  transformOrigin: "center center"
                }}
              >
                <div className="w-full h-full bg-slate-50 relative flex items-center justify-center">
                  <picture>
                    <img 
                      src={card.img} 
                      alt={card.name}
                      className="w-full h-full object-cover select-none pointer-events-none group-hover:scale-110 transition-transform duration-500"
                      referrerPolicy="no-referrer"
                      onError={(e) => {
                        e.target.src = `https://picsum.photos/seed/${card.id}/200/220`;
                      }}
                    />
                  </picture>
                  <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center pointer-events-none">
                    <span className="text-white font-bold text-center px-2 text-sm drop-shadow-md">
                      {card.name}
                    </span>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>

      {/* Navigation Bar (Floating Pill) */}
      <motion.header
        initial={{ y: -60, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        className="absolute z-50 flex items-center bg-[#FFFFFF] select-none top-[20px] md:top-[24px] lg:top-[30px] left-1/2 -translate-x-1/2 w-max pt-[10px] pr-[16px] pb-[10px] pl-[12px] rounded-full shadow-[0_8px_32px_rgba(0,0,0,0.03)] border border-slate-200/20"
      >
        <div className="flex items-center gap-2 pl-2">
          <div className="w-2 h-2 rounded-full bg-blue-600 animate-pulse" />
          <span className="font-bold text-slate-900 tracking-tight text-lg pr-2">Solutions</span>
        </div>
      </motion.header>

      {/* Main Content Area */}
      <div className="relative z-20 w-full max-w-[1312px] mx-auto flex flex-col justify-between flex-grow h-full px-[20px] md:px-[40px] lg:px-0 pt-[443px] md:pt-[559px] lg:pt-[100px]">
        <div className="hidden lg:block w-full h-[1px] min-h-[60px]" />
        
        <div className="w-full flex flex-col items-center justify-center text-center pb-[32px] lg:mt-auto mt-0">
          <motion.h1 
            initial={{ y: 50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="text-white font-medium text-center self-center text-[34px] sm:text-[46px] md:text-[58px] lg:text-[76px] tracking-[-1.5px] lg:tracking-[-3px] leading-[114%] w-full max-w-[820px] mx-auto px-0 lg:px-4"
          >
            Solutions for Every Business
          </motion.h1>
          
          <motion.p 
            initial={{ y: 40, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.4, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="text-white text-center font-normal mt-[16px] leading-[140%] tracking-[-0.3px] lg:tracking-[-0.8px] text-[15px] sm:text-[18px] md:text-[20px] lg:text-[22px] w-full max-w-[652px] mx-auto px-0 lg:px-4 opacity-95"
          >
            Industry-specific digital infrastructure tailored to your unique workflows and designed for smarter business growth.
          </motion.p>
          
          <motion.div 
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.6, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-[32px] w-full sm:w-auto px-0 lg:px-6"
          >
            <button className="group w-full sm:w-[220px] flex items-center justify-center gap-[10px] px-[20px] py-[14px] rounded-full font-bold text-base text-white bg-[#2563eb] hover:bg-[#1d4ed8] focus:outline-none focus:ring-4 focus:ring-[#2563eb]/40 active:scale-95 transition-all duration-200 shadow-sm select-none overflow-hidden relative">
              <div className="h-[24px] overflow-hidden relative w-full text-center">
                <span className="block transition-transform duration-500 ease-[cubic-bezier(0.76,0,0.24,1)] group-hover:-translate-y-full">Book a consultation</span>
                <span className="block absolute top-0 left-0 w-full translate-y-full transition-transform duration-500 ease-[cubic-bezier(0.76,0,0.24,1)] group-hover:translate-y-0">Book a consultation</span>
              </div>
            </button>
            <button className="group w-full sm:w-[200px] flex items-center justify-center gap-[10px] px-[20px] py-[14px] rounded-full font-bold text-base text-white border border-white/20 bg-white/10 backdrop-blur-md hover:bg-white/20 focus:outline-none focus:ring-4 focus:ring-white/25 active:scale-95 transition-all duration-200 shadow-sm select-none overflow-hidden relative">
              <div className="h-[24px] overflow-hidden relative w-full text-center">
                <span className="block transition-transform duration-500 ease-[cubic-bezier(0.76,0,0.24,1)] group-hover:-translate-y-full">Explore services</span>
                <span className="block absolute top-0 left-0 w-full translate-y-full transition-transform duration-500 ease-[cubic-bezier(0.76,0,0.24,1)] group-hover:translate-y-0">Explore services</span>
              </div>
            </button>
          </motion.div>
        </div>
      </div>
    </section>
  );
}